import { z } from "zod";
import { adminOperationsExtendedSchemaVersionSchema, adminUlidSchema, } from "./admin-operations-core.js";
/**
 * Every book, with how far it got and what its AI cost (admin assessment, 2026-10-15). Until
 * now a book could be found only by its 26-character id, and no page showed AI spend or
 * finished books. Everything is read from the books' own records, so past activity counts
 * too. Book content stays private: no titles, words or pictures.
 */
export const adminBookOverviewRequestSchema = z
    .object({
    limit: z.number().int().min(1).max(100).default(100),
    cursor: z.string().min(1).max(1024).optional(),
})
    .strict();
/** Business totals only: no customers and no books, so business partners can read them. */
export const adminBookTotalsRequestSchema = z.object({}).strict();
/** How far a book got, from the records its steps leave behind. */
export const adminBookStageSchema = z.enum([
    "STARTED",
    "STORY_PLANNED",
    "COVER_MADE",
    "FINISHED",
    "PRINT_APPROVED",
]);
/** How the book was paid for: the voucher's batch purpose, or the order's source. */
export const adminBookSourceSchema = z.enum([
    "DEMO_VOUCHER",
    "RETAIL_VOUCHER",
    /** A printed voucher from a batch made before batches recorded their purpose. */
    "VOUCHER",
    "EMAILED_VOUCHER",
    "OWNER_CHECK",
    "BOUGHT_ONLINE",
    "NO_VOUCHER",
    "UNKNOWN",
]);
const centsSchema = z.number().int().nonnegative();
export const adminBookOverviewEntrySchema = z
    .object({
    bookId: adminUlidSchema,
    reference: z.string().regex(/^[0-9A-HJKMNP-TV-Z]{8}$/u),
    editionId: z
        .string()
        .regex(/^[a-z0-9-]{1,40}$/u)
        .optional(),
    editionName: z.string().min(1).max(80).optional(),
    stage: adminBookStageSchema,
    /** False for book types whose steps aren't tracked yet (My Story): their stage means nothing. */
    progressTracked: z.boolean(),
    source: adminBookSourceSchema,
    test: z.boolean(),
    /** Why a book is a test: an automated run, a book started without a voucher, a test payment. */
    testReason: z.enum(["AUTOMATED_RUN", "EXAMPLE_BOOK", "TEST_PAYMENT"]).optional(),
    deletionPending: z.boolean(),
    createdAt: z.string().datetime().optional(),
    lastActivityAt: z.string().datetime().optional(),
    customerId: z.string().min(1).max(128).optional(),
    customerEmail: z.string().min(3).max(320).optional(),
    aiSpentCents: centsSchema,
    aiHeldCents: centsSchema,
    aiLimitCents: centsSchema.optional(),
})
    .strict();
export const adminBookTotalsSchema = z
    .object({
    books: z.number().int().nonnegative(),
    /** Books of types whose steps aren't tracked yet; they are left out of byStage. */
    untrackedBooks: z.number().int().nonnegative(),
    /** Books by the furthest step each reached. */
    byStage: z.record(adminBookStageSchema, z.number().int().nonnegative()),
    bySource: z.record(adminBookSourceSchema, z.number().int().nonnegative()),
    testBooks: z.number().int().nonnegative(),
    /** The test books among byStage and aiSpentCents, so real customers can be shown alone. */
    testByStage: z.record(adminBookStageSchema, z.number().int().nonnegative()),
    testAiSpentCents: centsSchema,
    aiSpentCents: centsSchema,
    aiHeldCents: centsSchema,
    /** AI spent on books that were finished, for a cost per finished book. */
    aiSpentOnFinishedCents: centsSchema,
    /** The test books' share of aiSpentOnFinishedCents (2026-10-21; absent from older servers). */
    testAiSpentOnFinishedCents: centsSchema.optional(),
    /** False when there were more books than one read covers; the totals are then partial. */
    complete: z.boolean(),
})
    .strict();
export const adminBookOverviewResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminOperationsExtendedSchemaVersionSchema,
        books: z.array(adminBookOverviewEntrySchema).max(100),
        /** Totals over the books on this page. */
        totals: adminBookTotalsSchema,
        nextCursor: z.string().min(1).max(1024).optional(),
    })
        .strict(),
})
    .strict();
export const adminBookTotalsResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: adminOperationsExtendedSchemaVersionSchema,
        totals: adminBookTotalsSchema,
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=admin-operations-books.js.map